import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  contentChild,
  effect,
  input,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { fieldErrorMessage } from '../utils/FormErrorUtils';
import { InputDirective } from './input.directive';

/**
 * Label + projected control + its hint and its one validation message.
 *
 * `hint` exists so explanatory text stops floating beside a field as a loose paragraph:
 * screens were putting "Minstens 6 tekens" or "Verlaat het veld om op te slaan" next to
 * an input with nothing tying the two together, so a screen reader announced the field
 * without its rule. The field wires `aria-describedby` itself, which is the whole point
 * of doing it here rather than in each template.
 */
@Component({
  // `display: block` on the host: an Angular component element is inline by default,
  // and Tailwind's space-y-* sets margin-top, which does nothing on an inline box. As
  // an inline host this rendered with no vertical gap at all between siblings.
  host: { class: 'block' },
  selector: 'app-field',
  // Eager: see error() below. The control it reads is not a signal.
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div>
      @if (label(); as text) {
        <label [attr.for]="for()" class="block text-sm/6 font-medium text-fg">
          {{ text }}
        </label>
      }
      <div class="mt-1.5"><ng-content /></div>
      @if (hint(); as text) {
        <p [id]="hintId()" class="mt-2 text-sm text-fg-muted">{{ text }}</p>
      }
      @if (error(); as message) {
        <p [id]="errorId()" class="mt-2 text-sm text-danger-fg">
          {{ message }}
        </p>
      }
    </div>
  `,
})
export class FieldComponent {
  readonly label = input<string>();
  readonly for = input<string>();
  readonly hint = input<string>();
  readonly control = input<AbstractControl | null>(null);

  /**
   * A method, not a computed, and the component is not OnPush.
   *
   * A reactive form control is a plain object whose identity never changes, so
   * `computed(() => control().touched)` reads its inputs once and caches the answer for
   * the life of the component. It cached "no error" before anyone had typed, and the app
   * therefore showed no validation message on any field on any screen: a member who
   * mistyped an e-mail got a dead submit button and no reason for it. Re-reading the
   * control on every check is what a form field is for, and it costs nothing.
   */
  protected error(): string | null {
    const c = this.control();
    return c && c.touched && c.dirty ? fieldErrorMessage(c.errors) : null;
  }

  protected hintId(): string | null {
    return this.hint() && this.for() ? `${this.for()}-hint` : null;
  }
  protected errorId(): string | null {
    return this.error() && this.for() ? `${this.for()}-error` : null;
  }

  private readonly projected = contentChild(InputDirective, {
    read: ElementRef,
  });

  constructor() {
    // The control is projected, so the template cannot bind an attribute onto it; this
    // is the one place that knows both the control and the ids describing it.
    effect(() => {
      const el = this.projected()?.nativeElement as HTMLElement | undefined;
      if (!el) {
        return;
      }
      const ids = [this.hintId(), this.errorId()].filter(Boolean).join(' ');
      if (ids) {
        el.setAttribute('aria-describedby', ids);
      } else {
        el.removeAttribute('aria-describedby');
      }
    });
  }
}
